import {
    showErrorNotifications,
    showSuccessNotification,
    showFileProcessingErrorNotification,
    showValidationErrorNotification
} from "../../../components/ServiceNotifications";

// Types for parsed domain data
export interface ParsedDomainData {
    domainName: string;
    expirationDate?: string;
    ownershipDate?: string;
    estimatedValue?: string;
    rowNumber: number;
}

export interface ValidationError {
    rowNumber: number;
    field: string;
    message: string;
    value: string;
}

export interface ValidationResult {
    valid: ParsedDomainData[];
    errors: ValidationError[];
    duplicates: string[];
}

export interface FileParseResult {
    data: ParsedDomainData[];
    errors: ValidationError[];
}

/**
 * Service for processing uploaded files containing domain data.
 * 
 * Supports CSV file parsing with comprehensive validation and error handling.
 * Provides data transformation and normalization for bulk import operations.
 * 
 * @example
 * ```typescript
 * const result = await fileProcessingService.parseFile(uploadedFile);
 * if (result.data.length > 0) {
 *   console.log(`Parsed ${result.data.length} domains`);
 * }
 * ```
 */
export class FileProcessingService {
    private readonly SUPPORTED_FORMATS = ['.csv', '.ods'];
    private readonly MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    // Column definitions for reference
    // private readonly REQUIRED_COLUMNS = ['Domain Name'];
    // private readonly OPTIONAL_COLUMNS = ['Expiration Date', 'Ownership Date', 'Estimated Value'];

    /**
     * Parse uploaded file and extract domain name data.
     * 
     * @param file - The uploaded file to process (CSV or ODS format)
     * @returns Promise resolving to parsed data and validation errors
     * 
     * @throws {Error} When file format is unsupported or file is too large
     * 
     * @example
     * ```typescript
     * const file = new File([csvContent], 'domains.csv', { type: 'text/csv' });
     * const result = await fileProcessingService.parseFile(file);
     * 
     * if (result.errors.length === 0) {
     *   console.log(`Successfully parsed ${result.data.length} domains`);
     * } else {
     *   console.log(`Found ${result.errors.length} validation errors`);
     * }
     * ```
     */
    async parseFile(file: File): Promise<FileParseResult> {
        try {
            // Validate file format and size
            this.validateFile(file);

            let data: ParsedDomainData[] = [];
            let errors: ValidationError[] = [];

            if (file.name.toLowerCase().endsWith('.csv')) {
                const result = await this.parseCsvFile(file);
                data = result.data;
                errors = result.errors;
            } else if (file.name.toLowerCase().endsWith('.ods')) {
                const errorMessage = 'ODS file format is not yet supported. Please use CSV format or convert your file to CSV.';
                showFileProcessingErrorNotification(errorMessage, file.name);
                throw new Error(errorMessage);
            } else {
                const errorMessage = `Unsupported file format. Please use ${this.SUPPORTED_FORMATS.join(' or ')} files.`;
                showFileProcessingErrorNotification(errorMessage, file.name);
                throw new Error(errorMessage);
            }

            if (data.length === 0 && errors.length === 0) {
                const errorMessage = 'No valid data found in the file. Please check that your file contains domain name data.';
                showFileProcessingErrorNotification(errorMessage, file.name);
                errors.push({
                    rowNumber: 0,
                    field: 'file',
                    message: errorMessage,
                    value: file.name
                });
            }

            // Show validation summary if there are errors
            if (errors.length > 0) {
                const errorSummary = this.generateErrorSummary(errors);
                showValidationErrorNotification(
                    `Found ${errors.length} validation error${errors.length !== 1 ? 's' : ''} in ${file.name}`,
                    errorSummary
                );
            }

            return { data, errors };
        } catch (error) {
            // Only show generic error notification if we haven't already shown a specific one
            if (!(error instanceof Error) || !error.message.includes('format') && !error.message.includes('supported')) {
                showFileProcessingErrorNotification(
                    error instanceof Error ? error.message : 'Unknown error occurred while processing file',
                    file.name
                );
            }
            throw error;
        }
    }

    /**
     * Validate domain name data and check for duplicates within file
     */
    validateDomainData(data: ParsedDomainData[]): ValidationResult {
        const valid: ParsedDomainData[] = [];
        const errors: ValidationError[] = [];
        const duplicates: string[] = [];
        const seenDomains = new Map<string, number>();

        for (const item of data) {
            const itemErrors = this.validateSingleDomainData(item);

            // Check for duplicates within the file
            const normalizedDomain = item.domainName.toLowerCase();
            if (seenDomains.has(normalizedDomain)) {
                if (!duplicates.includes(item.domainName)) {
                    duplicates.push(item.domainName);
                }
                // Don't add validation errors for duplicates here - let the duplicate resolution handle it
            } else {
                seenDomains.set(normalizedDomain, item.rowNumber);
            }

            // Only add to valid if no validation errors (duplicates will be handled separately)
            if (itemErrors.length === 0) {
                valid.push(item);
            } else {
                errors.push(...itemErrors);
            }
        }

        return { valid, errors, duplicates };
    }

    /**
     * Generate and download a template CSV file
     */
    downloadTemplate(): void {
        try {
            const headers = ['Domain Name', 'Expiration Date', 'Ownership Date', 'Estimated Value'];
            const sampleData = [
                ['example.com', '2025-12-31', '2020-01-15', '$1,000.00'],
                ['sample.org', '2026-06-30', '2019-03-20', '$500.00']
            ];

            const csvContent = this.generateCsvContent([headers, ...sampleData]);
            this.downloadFile(csvContent, 'domain-names-template.csv', 'text/csv');

            showSuccessNotification('Template file downloaded successfully');
        } catch (error) {
            showErrorNotifications(error);
        }
    }

    /**
     * Validate file format and size
     */
    private validateFile(file: File): void {
        // Check file size
        if (file.size > this.MAX_FILE_SIZE) {
            const errorMessage = `File size (${(file.size / (1024 * 1024)).toFixed(2)}MB) exceeds maximum limit of ${this.MAX_FILE_SIZE / (1024 * 1024)}MB. Please use a smaller file.`;
            showFileProcessingErrorNotification(errorMessage, file.name);
            throw new Error(errorMessage);
        }

        // Check if file is empty
        if (file.size === 0) {
            const errorMessage = 'File is empty. Please select a file with domain name data.';
            showFileProcessingErrorNotification(errorMessage, file.name);
            throw new Error(errorMessage);
        }

        // Check file format
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!this.SUPPORTED_FORMATS.includes(fileExtension)) {
            const errorMessage = `Unsupported file format "${fileExtension}". Please use ${this.SUPPORTED_FORMATS.join(' or ')} files.`;
            showFileProcessingErrorNotification(errorMessage, file.name);
            throw new Error(errorMessage);
        }
    }

    /**
     * Parse CSV file content
     */
    private async parseCsvFile(file: File): Promise<FileParseResult> {
        const text = await file.text();
        const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);

        if (lines.length === 0) {
            return {
                data: [],
                errors: [{
                    rowNumber: 0,
                    field: 'file',
                    message: 'File is empty',
                    value: file.name
                }]
            };
        }

        // Parse header row
        const headers = this.parseCsvRow(lines[0]);
        const columnMapping = this.mapColumns(headers);

        // Validate required columns
        const missingColumns = this.validateRequiredColumns(headers);
        if (missingColumns.length > 0) {
            return {
                data: [],
                errors: [{
                    rowNumber: 1,
                    field: 'headers',
                    message: `Missing required columns: ${missingColumns.join(', ')}`,
                    value: headers.join(', ')
                }]
            };
        }

        const data: ParsedDomainData[] = [];
        const errors: ValidationError[] = [];

        // Parse data rows
        for (let i = 1; i < lines.length; i++) {
            const rowNumber = i + 1;
            const row = this.parseCsvRow(lines[i]);

            if (row.length === 0) continue; // Skip empty rows

            try {
                const domainData = this.mapRowToDomainData(row, columnMapping, rowNumber);
                data.push(domainData);
            } catch (error) {
                errors.push({
                    rowNumber,
                    field: 'row',
                    message: error instanceof Error ? error.message : 'Failed to parse row',
                    value: lines[i]
                });
            }
        }

        return { data, errors };
    }

    /**
     * Parse a single CSV row, handling quoted values
     */
    private parseCsvRow(row: string): string[] {
        const result: string[] = [];
        let current = '';
        let inQuotes = false;
        let i = 0;

        while (i < row.length) {
            const char = row[i];

            if (char === '"') {
                if (inQuotes && row[i + 1] === '"') {
                    // Escaped quote
                    current += '"';
                    i += 2;
                } else {
                    // Toggle quote state
                    inQuotes = !inQuotes;
                    i++;
                }
            } else if (char === ',' && !inQuotes) {
                // End of field
                result.push(current.trim());
                current = '';
                i++;
            } else {
                current += char;
                i++;
            }
        }

        // Add the last field
        result.push(current.trim());

        return result;
    }

    /**
     * Map CSV headers to expected column positions
     */
    private mapColumns(headers: string[]): Record<string, number> {
        const mapping: Record<string, number> = {};

        headers.forEach((header, index) => {
            const normalizedHeader = header.trim().toLowerCase();

            if (normalizedHeader.includes('domain')) {
                mapping.domainName = index;
            } else if (normalizedHeader.includes('expiration')) {
                mapping.expirationDate = index;
            } else if (normalizedHeader.includes('ownership') || normalizedHeader.includes('purchase')) {
                mapping.ownershipDate = index;
            } else if (normalizedHeader.includes('value') || normalizedHeader.includes('price')) {
                mapping.estimatedValue = index;
            }
        });

        return mapping;
    }

    /**
     * Validate that required columns are present
     */
    private validateRequiredColumns(headers: string[]): string[] {
        const normalizedHeaders = headers.map(h => h.trim().toLowerCase());
        const missing: string[] = [];

        // Check for domain name column
        const hasDomainColumn = normalizedHeaders.some(h => h.includes('domain'));
        if (!hasDomainColumn) {
            missing.push('Domain Name');
        }

        return missing;
    }

    /**
     * Map a CSV row to domain data structure
     */
    private mapRowToDomainData(row: string[], columnMapping: Record<string, number>, rowNumber: number): ParsedDomainData {
        const domainName = columnMapping.domainName !== undefined ? row[columnMapping.domainName]?.trim() : '';

        if (!domainName) {
            throw new Error('Domain name is required');
        }

        return {
            domainName,
            expirationDate: columnMapping.expirationDate !== undefined ? row[columnMapping.expirationDate]?.trim() || undefined : undefined,
            ownershipDate: columnMapping.ownershipDate !== undefined ? row[columnMapping.ownershipDate]?.trim() || undefined : undefined,
            estimatedValue: columnMapping.estimatedValue !== undefined ? row[columnMapping.estimatedValue]?.trim() || undefined : undefined,
            rowNumber
        };
    }

    /**
     * Validate a single domain data item
     */
    private validateSingleDomainData(item: ParsedDomainData): ValidationError[] {
        const errors: ValidationError[] = [];

        // Validate domain name format
        if (!this.isValidDomainName(item.domainName)) {
            errors.push({
                rowNumber: item.rowNumber,
                field: 'domainName',
                message: 'Invalid domain name format',
                value: item.domainName
            });
        }

        // Validate expiration date format
        if (item.expirationDate && !this.isValidDate(item.expirationDate)) {
            errors.push({
                rowNumber: item.rowNumber,
                field: 'expirationDate',
                message: 'Invalid date format. Expected YYYY-MM-DD',
                value: item.expirationDate
            });
        }

        // Validate ownership date format
        if (item.ownershipDate && !this.isValidDate(item.ownershipDate)) {
            errors.push({
                rowNumber: item.rowNumber,
                field: 'ownershipDate',
                message: 'Invalid date format. Expected YYYY-MM-DD',
                value: item.ownershipDate
            });
        }

        return errors;
    }

    /**
     * Validate domain name format
     */
    private isValidDomainName(domain: string): boolean {
        if (!domain || domain.length === 0) return false;

        // Basic domain name validation regex
        const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

        return domainRegex.test(domain) && domain.length <= 253;
    }

    /**
     * Validate date format (YYYY-MM-DD)
     */
    private isValidDate(dateString: string): boolean {
        if (!dateString) return false;

        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(dateString)) return false;

        const date = new Date(dateString);
        return date instanceof Date && !isNaN(date.getTime()) && dateString === date.toISOString().split('T')[0];
    }

    /**
     * Generate CSV content from data array
     */
    private generateCsvContent(data: string[][]): string {
        return data.map(row =>
            row.map(cell => {
                // Escape quotes and wrap in quotes if contains comma or quote
                if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
                    return `"${cell.replace(/"/g, '""')}"`;
                }
                return cell;
            }).join(',')
        ).join('\n');
    }

    /**
     * Generate error summary for user feedback
     */
    private generateErrorSummary(errors: ValidationError[]): string[] {
        const summary: string[] = [];
        const errorsByType = new Map<string, number>();
        const sampleErrors: string[] = [];

        // Categorize errors
        errors.forEach(error => {
            const key = `${error.field}: ${error.message}`;
            errorsByType.set(key, (errorsByType.get(key) || 0) + 1);
            
            // Keep first few examples
            if (sampleErrors.length < 3) {
                sampleErrors.push(`Row ${error.rowNumber}: ${error.message} (${error.value})`);
            }
        });

        // Add error type summary
        errorsByType.forEach((count, errorType) => {
            summary.push(`${errorType} (${count} occurrence${count !== 1 ? 's' : ''})`);
        });

        // Add sample errors if we have them
        if (sampleErrors.length > 0) {
            summary.push('', 'Examples:');
            summary.push(...sampleErrors);
        }

        return summary;
    }

    /**
     * Download file to user's computer
     */
    private downloadFile(content: string, filename: string, mimeType: string): void {
        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    }
}

// Export singleton instance
export const fileProcessingService = new FileProcessingService();
export default fileProcessingService;